import React, { useState } from 'react';
import { Modal, Form, Select, Input, message, Card, Avatar, Space, Typography, Divider, Button } from 'antd';
import { UserOutlined, MessageOutlined, SendOutlined, CloseOutlined } from '@ant-design/icons';
import { useRequestComment } from '../../hooks/memo/memoQuery';
import { useSearchUsers } from '../../hooks/user/userQuery';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

export default function RequestCommentModal({ visible, onClose, memo, onSuccess }) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [searchText, setSearchText] = useState('');

    const requestComment = useRequestComment();
    const searchUsers = useSearchUsers();

    // Search users when search text changes
    const handleUserSearch = async (value) => {
        setSearchText(value);
        if (value.length >= 2) {
            try {
                const usersData = await searchUsers(value);
                setUsers(usersData || []);
            } catch (error) {
                console.error('Error searching users:', error);
                message.error('Failed to search users');
            }
        } else {
            setUsers([]);
        }
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const payload = {
                memo: memo.id,
                userId: values.userId,
                message: values.message || ''
            };

            const response = await requestComment(payload);
            message.success('Comment request sent successfully');
            form.resetFields();
            onSuccess && onSuccess(response);
            onClose();
        } catch (error) {
            console.error('Error requesting comment:', error);
            message.error('Failed to send comment request');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    return (
        <Modal
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={650}
            centered
            closable={false}
            className="modern-modal"
            styles={{
                body: { padding: 0 },
                header: { display: 'none' }
            }}
        >
            <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '24px',
                borderRadius: '8px 8px 0 0'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Avatar
                            size={40}
                            style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                color: 'white'
                            }}
                            icon={<MessageOutlined />}
                        />
                        <div>
                            <Title level={4} style={{ color: 'white', margin: 0 }}>
                                Request Comment
                            </Title>
                            <Text style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
                                Get input from a colleague before proceeding
                            </Text>
                        </div>
                    </div>
                    <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={handleCancel}
                        style={{
                            color: 'white',
                            border: 'none',
                            background: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    />
                </div>
            </div>

            <div style={{ padding: '24px' }}>
                <Card
                    style={{
                        marginBottom: '24px',
                        border: '1px solid #f0f0f0',
                        borderRadius: '8px',
                        background: '#fafafa'
                    }}
                    bodyStyle={{ padding: '16px' }}
                >
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
                            {memo?.title}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                            Request feedback from a colleague to ensure thorough review before approval
                        </Text>
                    </Space>
                </Card>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    style={{ marginBottom: '24px' }}
                >
                    <Form.Item
                        name="userId"
                        label={
                            <Text strong style={{ fontSize: '16px' }}>
                                <UserOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                                Request Comment From
                            </Text>
                        }
                        rules={[{ required: true, message: 'Please select a user' }]}
                    >
                        <Select
                            placeholder="Type to search users..."
                            showSearch
                            onSearch={handleUserSearch}
                            filterOption={false}
                            notFoundContent={searchText.length >= 2 ? 'No users found' : 'Type at least 2 characters to search'}
                            size="large"
                            style={{ borderRadius: '8px' }}
                            optionLabelProp="label"
                        >
                            {users.map(user => (
                                <Option 
                                    key={user.userId} 
                                    value={user.userId}
                                    label={`${user.name} (${user.userId})`}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                                        <Avatar size={24} icon={<UserOutlined />} />
                                        <div>
                                            <div style={{ fontWeight: '500', fontSize: '14px' }}>{user.name}</div>
                                            <div style={{ fontSize: '12px', color: '#666' }}>ID: {user.userId}</div>
                                        </div>
                                    </div>
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="message"
                        label={
                            <Text strong style={{ fontSize: '16px' }}>
                                <MessageOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                                Message (Optional)
                            </Text>
                        }
                    >
                        <TextArea
                            rows={4}
                            placeholder="Add an optional message explaining what specific input you need..."
                            maxLength={500}
                            showCount
                            style={{
                                borderRadius: '8px',
                                fontSize: '14px'
                            }}
                        />
                    </Form.Item>
                </Form>

                <Divider style={{ margin: '24px 0' }} />

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <Button
                        size="large"
                        onClick={handleCancel}
                        style={{
                            borderRadius: '8px',
                            height: '44px',
                            paddingLeft: '24px',
                            paddingRight: '24px'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        size="large"
                        loading={loading}
                        onClick={() => form.submit()}
                        icon={<SendOutlined />}
                        style={{
                            borderRadius: '8px',
                            height: '44px',
                            paddingLeft: '24px',
                            paddingRight: '24px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none'
                        }}
                    >
                        Send Request
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
